import { connect } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FC, useEffect, useState } from 'react';
import { YellowClock } from 'tedooo-web-design-system/icons';
import { Button, LoaderIndicator } from 'tedooo-web-design-system/ui';

import CoverPhoto from 'photos/coverPhoto.png';
import Header from './header/Header';
import {
  CreateShopSteps,
  DrawerIdsOptions,
  IShopPage,
  IStore2,
  popupIdsOptions,
  ShopAuthorizationStatus,
  ShopTabsOptions,
} from 'types';
import { editCoverPhoto as editCover, getShop, getShopMojos } from 'store/shop/ShopActions';
import { getReviews } from 'store/review/ReviewActions';
import { UserSelector } from 'store/user/UserSelector';
import { ROUTES, SEARCH_PARAMS } from 'constant';
import './shopPage.scss';
import Content from './content/Content';
import { ShopPageTabs } from './helper';
import { PopupSlicer } from 'store/popup/PopupActions';
import { DrawerSlicer } from 'store/drawers/DrawerActions';
import { useRequireToken } from 'hooks/useRequireToken';
import { CreateShopSlicer } from 'store/createShop/CreateShopSlicer';
import { translations } from 'translations/en';
import { updateAvatar } from 'store/user/UserActions';
import { getSuggestedShops, onFollowUserToggle } from 'store/profile/ProfileActions';
import { buildChatUrl, isEmptyArrayOrUndefined, scrollToTopOfPage } from 'utils';
import { ShopSelector } from 'store/shop/ShopSelector';
import { superAdminMarkAsCrystalShop } from 'store/searchResult/SearchResultActions';
import { getShopFeed as getFeed } from 'store/feed/FeedActions';
import { FeedSelector } from 'store/feed/FeedSelectors';
import { FeedSlicer } from '../../store/feed/FeedSlicer';

const ShopPage: FC<IShopPage> = ({
  shop,
  editShop,
  editCoverPhoto,
  editAvatar,
  details,
  tip,
  onCsvReset,
  suggestedShops,
  onFollowToggle,
  getSpecificShopReviews,
  token,
  getSpecificShop,
  loggedUserId,
  showPopup,
  showDrawer,
  getShopSuggestions,
  onFollow,
  getUserFeed,
  getUserMojos,
  isSuperAdmin,
  adminMarkShop,
  resetUserFeed,
  postsDetails,
}) => {
  const params = useParams();

  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const initialTab = ShopPageTabs[searchParams.get(SEARCH_PARAMS.SHOP_TAB) || 0]?.key || '0';

  const [selectedTab, setSelectedTab] = useState(initialTab);

  const getUserPosts = () => {
    if (token && shop.details?.userId) {
      getUserFeed({ token, userId: shop.details?.userId });
    }
  };

  const tabHandler = (index: string) => {
    scrollToTopOfPage();
    setTimeout(() => {
      setSelectedTab(ShopPageTabs[index].key);
    }, 0);
  };

  const onTabChangeWithTokenCheck = (index: string) => {
    tabHandler(index);
  };

  const onTabChangeWithoutTokenCheck = useRequireToken((index: string) => {
    tabHandler(index);
  });

  const onTabChange = (index: string) => {
    const { isTokenRequired } = ShopPageTabs[index];

    if (isTokenRequired) {
      onTabChangeWithoutTokenCheck(index);
    } else {
      onTabChangeWithTokenCheck(index);
    }
  };

  useEffect(() => {
    const { shopId } = params;

    if (shopId) {
      if (shopId !== shop.details?.id) {
        getSpecificShop({ id: shopId });
        getSpecificShopReviews({ id: shopId });
      }
    }
  }, [params?.shopId, token]);

  useEffect(() => {
    if (
      shop.details &&
      !postsDetails.list &&
      postsDetails.hasMore &&
      selectedTab === ShopTabsOptions.Activity
    ) {
      getUserPosts();
    }
  }, [shop.details, selectedTab]);

  useEffect(() => {
    onCsvReset();

    return () => {
      resetUserFeed();
    };
  }, [params?.shopId]);
  useEffect(() => {
    if (selectedTab === ShopTabsOptions.Activity) {
      if (isEmptyArrayOrUndefined(suggestedShops) && details?.userId) {
        getShopSuggestions(details.userId);
      }
    }

    if (selectedTab === ShopTabsOptions.Home) {
      if (initialTab !== ShopTabsOptions.Home) {
        navigate('');
      }
    } else {
      navigate(`?${SEARCH_PARAMS.SHOP_TAB}=${selectedTab}`);
    }
  }, [selectedTab]);

  const onMessageClick = useRequireToken(() => {
    if (details) {
      const { userId } = details;

      const { username, avatar, fullName } = details.userDetails;

      const chatDetails = {
        userId,
        username,
        avatar,
        fullName,
      };

      const url = buildChatUrl(chatDetails);
      navigate(url);
    }
  });

  const onTabUpdate = (tab: ShopTabsOptions) => {
    scrollToTopOfPage();
    setSelectedTab(tab);
  };

  const onActivitiesClick = useRequireToken(() => {
    onTabUpdate(ShopTabsOptions.Activity);
  });

  const onReviewsClick = useRequireToken(() => {
    onTabUpdate(ShopTabsOptions.Reviews);
  });

  const onSeeAllCatalogClick = () => {
    onTabUpdate(ShopTabsOptions.Catalog);
  };

  const onFollowClick = useRequireToken(() => {
    onFollowToggle({
      shopId: details?.id!,
      userId,
      isFollowing,
    });
  });

  if (!details) {
    return <LoaderIndicator className="shop-loading-indicator" size={30} color="#C9CBCA" />;
  }

  const {
    isPremium,
    coverImage,
    averageRating,
    totalFollowers,
    totalFollowing,
    totalReviews,
    isFollowing,
    name,
    country,
    city,
    shop_number,
    userId,
    isMarkedAsCrystalShop,
    countries,
    userDetails,
    vacationDetails,
    isReviewEnabled,
    isVisionSeller,
  } = details;

  const coverPhoto = coverImage || CoverPhoto;

  const { username } = details.userDetails;

  const storeName = name || `Shop number ${shop_number}`;

  const location = `${city ? `${city}, ` : ''}${country}`;

  const isShopOwner = loggedUserId === userId;

  const getMojos = () => {
    getUserMojos({ id: userId });
  };

  const onMoreClick = () => {
    showDrawer({ id: DrawerIdsOptions.ShopMoreDrawer });
  };

  const onReviewClick = () => {
    showPopup({ id: popupIdsOptions.getTheAppPopup });
  };

  const onEditProfileClick = () => {
    editShop({ shop, step: CreateShopSteps.Initial });
    if (shop.details) {
      navigate(`/${ROUTES.CREATE_SHOP}`);
    }
  };

  const onAddItemToCatalogClick = () => {
    if (shop.details) {
      showPopup({
        id: popupIdsOptions.addListingPopupShop,
        shopId: shop.details.id,
      });
    }
  };

  const onFollowersClick = () => {
    if (isShopOwner) {
      showPopup({ id: popupIdsOptions.followersPopup });
    }
  };

  const isPendingShop = details.authorizationStatus === ShopAuthorizationStatus.Pending;

  const onSuperAdminMarkClick = () => {
    adminMarkShop({ shopId: details.id });
  };

  const markButtonText = isMarkedAsCrystalShop ? 'Marked' : 'Mark';

  return (
    <div className="page shop-page-container">
      <div className="preview column full-width">
        <div className="section">
          {isPendingShop && (
            <div className="pending-container">
              <YellowClock />
              <span className="pending-message">{translations.SHOP.PENDING_MESSAGE}</span>
            </div>
          )}
          {isSuperAdmin && (
            <Button
              type="primary"
              size="medium"
              text={markButtonText}
              borderRadius="small"
              onClick={onSuperAdminMarkClick}
            />
          )}
          <Header
            userName={username}
            userId={userId}
            avatar={userDetails.avatar}
            isShopOwner={isShopOwner}
            selectedTab={selectedTab}
            onTabClick={onTabChange}
            onFollowToggle={onFollowClick}
            onMessage={onMessageClick}
            shopName={storeName}
            shippingCountries={countries}
            originCountry={country}
            isReviewEnabled={isReviewEnabled}
            location={location}
            rate={averageRating}
            isPremium={isPremium}
            totalFollowers={totalFollowers}
            totalFollowing={totalFollowing}
            totalReviews={totalReviews}
            image={coverPhoto}
            isFollowing={isFollowing}
            onReviewClick={onReviewClick}
            onAddItemToCatalogClick={onAddItemToCatalogClick}
            onEditProfileClick={onEditProfileClick}
            onMoreClick={onMoreClick}
            onFollowersClick={onFollowersClick}
            editCoverPhoto={editCoverPhoto}
            editAvatar={editAvatar}
            isVisionSeller={isVisionSeller}
          />
        </div>
        <Content
          tip={tip}
          postsDetails={postsDetails}
          vacationDetails={vacationDetails}
          userId={userId}
          suggestedShops={suggestedShops}
          isShopOwner={isShopOwner}
          onSeeAllCatalogClick={onSeeAllCatalogClick}
          onReviewsClick={onReviewsClick}
          onActivitiesClick={onActivitiesClick}
          selectedTab={selectedTab}
          onFollow={onFollow}
          getUserFeed={getUserPosts}
          getUserMojos={getMojos}
          onAddItemToCatalogClick={onAddItemToCatalogClick}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state: IStore2) => {
  const {
    shop,
    shop: { details },
  } = state;

  const { uploadPercentage } = state.createShop.csvUploadInformation;

  const { suggestedShops: shops } = state.profile;

  const suggestedShops = shops ? shops.slice(0, 3) : [];

  const token = UserSelector.tokenSelector(state);

  const loggedUserId = UserSelector.loggedUserIdSelector(state);

  const postsDetails = FeedSelector.shopPostsSelector(state);

  const tip = ShopSelector.shopTipSelector(state);

  const { isSuperAdmin } = state.user;

  return {
    details,
    token,
    loggedUserId,
    shop,
    csvUploadPercentage: uploadPercentage,
    suggestedShops,
    tip,
    postsDetails,
    isSuperAdmin,
  };
};

const mapDispatchToProps = {
  onFollow: onFollowUserToggle,
  getShopSuggestions: getSuggestedShops,
  getSpecificShop: getShop,
  onFollowToggle: onFollowUserToggle,
  showPopup: PopupSlicer.actions.show,
  showDrawer: DrawerSlicer.actions.show,
  getSpecificShopReviews: getReviews,
  addItemToCatalog: CreateShopSlicer.actions.navigateToAddListingForm,
  editShop: CreateShopSlicer.actions.editShop,
  editCoverPhoto: editCover,
  getUserFeed: getFeed,
  getUserMojos: getShopMojos,
  resetUserFeed: FeedSlicer.actions.resetShopFeed,
  editAvatar: updateAvatar,
  adminMarkShop: superAdminMarkAsCrystalShop,
  onCsvReset: CreateShopSlicer.actions.reset,
};

export default connect(mapStateToProps, mapDispatchToProps)(ShopPage);
